import { Controller } from '@hotwired/stimulus'
import LegacyAPI from '../app/modules/legacy_api'
import { get, post, put } from '@rails/request.js'

/**
 * Handles remote CSV export with automatic progress polling and download.
 * Replaces CSVExport module's remote download functionality.
 *
 * Usage:
 *   <button
 *     data-controller="export-report-button"
 *     data-action="click->export-report-button#export"
 *     data-export-report-button-url-value="/reports/calls"
 *     data-export-report-button-params-value='{"filter": "value"}'
 *     data-export-report-button-auto-open-value="true">
 *     CSV Export
 *   </button>
 *
 * Workflow:
 *   1. User clicks button
 *   2. POST to url with params to create report
 *   3. Poll url/{id}.json every 1s for progress
 *   4. Update button text with percentage
 *   5. On completion: auto-download file or show download link
 */
export default class extends Controller {
    static values = {
        url: String,
        params: { type: Object, default: {} },
        autoOpen: { type: Boolean, default: true },
    }

    static targets = ['text', 'spinner']

    connect() {
        this.pollingTimeout = null
        this.reportId = null
        this.originalButtonText = this.element.textContent || ''
    }

    disconnect() {
        this.stopPolling()
    }

    /**
     * Main export action - creates report and starts polling
     */
    async export(event) {
        event?.preventDefault()

        // Prevent multiple simultaneous exports
        if (this.pollingTimeout) {
            this.showError('Export already in progress')
            return
        }

        this.disableButton()
        this.updateButtonText()

        try {
            await this.createReport()
        } catch (error) {
            console.error('ExportReportButton: failed to create report', error)
            this.enableButton()
        }
    }

    /**
     * POST to create the report
     */
    async createReport() {
        // Build form data in Rails format: report[params][key]=value
        // NOTE: We're doing this instead of sending JSON because Reports::Base expects
        // the params as a strings (ie, "true" not true), not an object. Leaving it this
        // way for backwards compatibility.
        const formData = new FormData()
        formData.append('report[format]', 'csv')
        formData.append('report[notify_user]', 'true')

        // Add nested params: report[params][search], report[params][page], etc.
        for (const [key, value] of Object.entries(this.paramsValue)) {
            if (Array.isArray(value)) {
                // Handle arrays: report[params][campaign_id][]
                value.forEach((item) => {
                    formData.append(`report[params][${key}][]`, String(item))
                })
            } else if (value !== null && value !== undefined) {
                formData.append(`report[params][${key}]`, String(value))
            }
        }

        const response = await post(`${this.urlValue}.json`, {
            body: formData,
            responseKind: 'json',
        })

        if (!response.ok) {
            const error = new Error('Export report creation failed')
            if (window.newrelic) window.newrelic.noticeError(error, { response })
            this.showError('Failed to create export')
            throw error
        }

        const data = await response.json

        if (data.errors && Object.keys(data.errors).length > 0) {
            this.showError(Object.values(data.errors).join(', '))
            throw new Error('Export report has errors')
        }

        this.reportId = data.id
        this.startPolling()
    }

    /**
     * Begin polling cycle
     */
    startPolling() {
        if (!this.reportId) return
        this.poll()
    }

    /**
     * Stop polling
     */
    stopPolling() {
        if (this.pollingTimeout) {
            clearTimeout(this.pollingTimeout)
            this.pollingTimeout = null
        }
    }

    /**
     * Poll the report status
     */
    async poll() {
        try {
            const response = await get(`${this.urlValue}/${this.reportId}.json`, {
                responseKind: 'json',
            })

            if (!response.ok) {
                this.showError('Export failed')
                if (window.newrelic) {
                    const error = new Error('Export polling failed')
                    window.newrelic.noticeError(error, { response })
                }
                return
            }

            const data = await response.json

            if (data.in_progress) {
                // Update progress and continue polling
                this.updateButtonText(data.export_percentage)
                this.pollingTimeout = setTimeout(() => this.poll(), 1000)
            } else {
                // Export complete
                this.handleComplete(data)
            }
        } catch (error) {
            console.error('ExportReportButton: poll error', error)
            if (window.newrelic) window.newrelic.noticeError(error)
            this.showError('Export error')
        }
    }

    /**
     * Handle completed export
     */
    async handleComplete(data) {
        this.stopPolling()

        const downloadUrl = data.file_upload_url

        if (!downloadUrl) {
            console.error('ExportReportButton: file_upload_url not found in response')
            this.showError('Download URL not available')
            return
        }

        if (this.autoOpenValue) {
            // Disable email notification and auto-download
            await this.disableEmailNotification()
            this.downloadFile(downloadUrl)
            this.enableButton()
        } else {
            // Convert button to download link
            this.replaceWithDownloadLink(downloadUrl)
        }
    }

    /**
     * Disable email notification by clearing recipients
     */
    async disableEmailNotification() {
        try {
            await put(`${this.urlValue}/${this.reportId}.json`, {
                body: {
                    report: {
                        recipients_csv: '',
                    },
                },
                responseKind: 'json',
            })
        } catch (error) {
            console.error('ExportReportButton: failed to disable email notification', error)
        }
    }

    /**
     * Download file in hidden iframe
     */
    downloadFile(fileUrl) {
        const iframe = document.createElement('iframe')
        iframe.style.height = '0px'
        iframe.style.width = '0px'
        iframe.style.border = '0px'
        iframe.src = fileUrl
        document.body.appendChild(iframe)
    }

    /**
     * Replace button with download link
     */
    replaceWithDownloadLink(downloadUrl) {
        this.element.href = downloadUrl
        this.element.target = '_blank'
        this.element.title = 'Download'
        this.element.classList.remove('disabled', 'pe-none', 'btn-light')
        this.element.classList.add('btn-success')

        if (this.hasSpinnerTarget) {
            this.spinnerTarget.classList.add('d-none')
        }

        if (this.hasTextTarget) {
            this.textTarget.textContent = 'Download'
        } else {
            this.element.textContent = 'Download'
        }
    }

    /**
     * Update button text with progress
     */
    updateButtonText(percentage) {
        if (!this.element) return

        const progressNumber = Number(percentage)
        const text =
            Number.isFinite(progressNumber) && progressNumber > 0
                ? `Exporting... (${Math.round(progressNumber)}%)`
                : 'Exporting...'

        if (this.hasTextTarget) {
            this.textTarget.textContent = text
        } else {
            this.element.textContent = text
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        if (this.hasTextTarget) {
            this.textTarget.textContent = 'Error'
        } else {
            this.element.textContent = 'Error'
        }

        // Flash notification
        LegacyAPI.flashNotice(message, 'danger')
    }

    /**
     * Disable button
     */
    disableButton() {
        if (this.element) {
            this.element.disabled = true
        }
    }

    /**
     * Enable button and restore text
     */
    enableButton() {
        if (this.element) {
            this.element.disabled = false
            this.element.textContent = this.originalButtonText
        }
    }
}
